import React, { memo, useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Col, PageSection, Row, Section, Tab, Tabs, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { sendTabEvent } from 'reaxl-analytics-handlers';

import vdpRatingReviewDuck from '@/ducks/vdp/vdpRatingReviewDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import { ConsumerRatings } from '@/components/RatingsReviews';

import ConsumerReview from './ConsumerReview';
import ConsumerReviewModal from './ConsumerReviewModal';
import ExpertReview from './ExpertReview';

function SectionWrapper({ isInColumn, children, ...rest }) {
    return isInColumn ? (
        <Section
            className="margin-vertical-5"
            {...rest}
        >
            {children}
        </Section>
    ) : (
        <PageSection
            className="col-lg-8"
            {...rest}
        >
            {children}
        </PageSection>
    );
}

function RatingsAndReviewsContainer({
    isInColumn,
}) {
    const dispatch = useDispatch();

    const { kbbConsumerReviewCount, kbbVehicleId, make, model, trim } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const {
        consumerRatings = {},
        consumerReviews,
        expertReview,
    } = useSelector(vdpRatingReviewDuck.selectors.getDuckState) || {};
    const averageRatings = useSelector(vdpRatingReviewDuck.selectors.getAverageRatings) || {};

    const [reviewsFetched, setReviewsFetched] = useState(false);

    const handleTabClick = useCallback((tabIndex, event) => {
        const tabs = ['consumer-reviews', 'expert-review'];
        sendClick(sendTabEvent, event, { componentType: 'cta', label: tabs[tabIndex] });
    }, []);

    useEffect(() => {
        // get consumer reviews
        if (kbbVehicleId && !reviewsFetched) {
            dispatch(vdpRatingReviewDuck.creators.hydrateConsumerReviews(kbbVehicleId));
            dispatch(vdpRatingReviewDuck.creators.hydrateExpertReviews(kbbVehicleId));
            setReviewsFetched(true);
        }
    }, [dispatch, kbbVehicleId, reviewsFetched]);

    const toggleShowModal = () => {
        dispatch(vdpRatingReviewDuck.creators.setShowReviewsModal(true));
    };

    const renderTabs = useCallback(() => {
        const tabs = [
            <Tab
                key="consumer-review"
                eventKey={0}
                title="Consumer Reviews"
            >
                <ConsumerReview />
            </Tab>,
        ];

        if (expertReview && Object.keys(expertReview).length) {
            tabs.push(
                <Tab
                    key="expert-review"
                    eventKey={1}
                    title="Expert Review"
                >
                    <ExpertReview />
                </Tab>
            );
        }

        return tabs;
    }, [expertReview]);

    // if kbbConsumerReviewCount has value then review content should exist
    return !!kbbConsumerReviewCount && (
        <div
            id="rating-reviews-section"
        >
            {reviewsFetched && consumerReviews?.length > 0 && (
                <SectionWrapper
                    isInColumn={isInColumn}
                    key="rating-reviews-section"
                    alignment="left"
                    data-cmp="ratingsAndReviewsContainer"
                    heading={(
                        <>
                            What people are saying about the
                            {' '}
                            {make?.name ?? make}
                            {' '}
                            {model?.name ?? model}
                            {' '}
                            {trim?.name ?? trim}
                        </>
                    )}
                    subheading={(
                        <>
                            <Text>
                                KBB.com
                                <sup>&#174;</sup>
                            </Text>
                            {' '}
                            Consumer Ratings & Reviews
                        </>
                    )}
                    uiContext="level-3"
                >
                    <Row>
                        <Col
                            xs={12}
                            md={6}
                        >
                            <ConsumerRatings
                                className="padding-bottom-4"
                                onReviewsDetailClick={toggleShowModal}
                                overallRating={consumerRatings.averageOverallRating}
                                ratings={averageRatings}
                                ratingBreakdownClassName="margin-top-4"
                                reviewCounts={consumerRatings.totalReviewCount}
                            />
                        </Col>
                        <Tabs
                            defaultActiveKey={0}
                            className="nav-tabs-light col-xs-12 col-md-6"
                            alignment="left"
                            onSelect={handleTabClick}
                        >
                            {renderTabs()}
                        </Tabs>
                    </Row>
                    <ConsumerReviewModal />
                </SectionWrapper>
            )}
        </div>
    );
}

export default memo(RatingsAndReviewsContainer);
